import React, { useEffect, useState } from "react";
import axios from "axios";
import PageHeader from "app/components/page/Header";
import SummaryCards, { SummaryCard }
  from "app/components/summary/SummaryCards";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// Endpoint da API ajustado
const ZABBIX_API = "https://dev.gbonline.com.br/traffic/all";

// Tipos de tráfego (entrada / saída)
const TIPO_ENTRADA = "entrada";
const TIPO_SAIDA = "saida";

// Intervalos disponíveis (devem coincidir com o backend)
const RANGE_OPTIONS = [
  "5m",
  "30m",
  "1h",
  "3h",
  "6h",
  "24h",
];
//  "3d",
//  "7d",
// Os itens de interface do Zabbix são taxas em bits por segundo.
function formatBitrate(bitsPerSecond) {
  const value = Number(bitsPerSecond);
  if (!Number.isFinite(value) || value === 0) return "0 bps";
  const units = ["bps", "Kbps", "Mbps", "Gbps", "Tbps"];
  const unitIndex = Math.min(
    Math.floor(Math.log(Math.abs(value)) / Math.log(1000)),
    units.length - 1
  );
  const normalized = value / Math.pow(1000, Math.max(0, unitIndex));
  return `${normalized.toLocaleString("pt-BR", {
    maximumFractionDigits: normalized >= 100 ? 0 : 2,
  })} ${units[Math.max(0, unitIndex)]}`;
}

// Une dados de entrada + saída (merge) por timestamp aproximado
function mergeData(entradaData, saidaData) {
  const samples = new Map();
  const addSamples = (items, key) => {
    items.forEach((item) => {
      const timestamp = Number(item.clock);
      const bucket = Math.round(timestamp / 5) * 5;
      const current = samples.get(bucket) || { timestamp: bucket };
      current[key] = Number(item.value);
      samples.set(bucket, current);
    });
  };

  addSamples(entradaData, "rx");
  addSamples(saidaData, "tx");

  return Array.from(samples.values()).sort(
    (first, second) => first.timestamp - second.timestamp
  );
}

const TrafficAggregation = () => {
  const [data, setData] = useState([]);
  const [selectedRange, setSelectedRange] = useState("1h");
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("both");
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchData = async (range) => {
    try {
      setLoading(true);
      setError(null);

      //  Requisição para "entrada"
      const entradaPromise = axios.post(ZABBIX_API, {
        lastTime: range,
        tipo: TIPO_ENTRADA,
      });

      //  Requisição para "saida"
      const saidaPromise = axios.post(ZABBIX_API, {
        lastTime: range,
        tipo: TIPO_SAIDA,
      });

      const [entradaRes, saidaRes] = await Promise.all([
        entradaPromise,
        saidaPromise,
      ]);

      const entradaData = entradaRes.data?.result || [];
      const saidaData = saidaRes.data?.result || [];

      const merged = mergeData(entradaData, saidaData);
      setData(merged);
      setUpdatedAt(new Date());
    } catch (err) {
      console.error("Erro ao buscar dados Zabbix:", err);
      setError("Não foi possível carregar os dados de tráfego.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(selectedRange);
  }, [selectedRange]);

  const visibleKeys = viewMode === "both" ? ["rx", "tx"] : [viewMode];
  const visibleValues = data.flatMap((sample) =>
    visibleKeys.map((key) => sample[key]).filter(Number.isFinite)
  );
  const currentSample = data[data.length - 1] || {};
  const currentValue = viewMode === "both"
    ? (currentSample.rx || 0) + (currentSample.tx || 0)
    : currentSample[viewMode];
  const average = visibleValues.length
    ? visibleValues.reduce((total, value) => total + value, 0) / visibleValues.length
    : null;
  const peak = visibleValues.length ? Math.max(...visibleValues) : null;

  return (
    <div className="traffic-page">
      <PageHeader
        title="Tráfego agregado"
        subtitle={updatedAt
          ? `Última atualização às ${updatedAt.toLocaleTimeString("pt-BR")}`
          : "Entrada e saída agregadas do PTT GB Online."} />

      <SummaryCards ariaLabel="Resumo do tráfego">
        <SummaryCard
          label="Tráfego atual"
          value={loading ? "—" : formatBitrate(currentValue)}
          helper={viewMode === "both" ? "entrada + saída" : viewMode === "rx" ? "entrada" : "saída"} />
        <SummaryCard
          label="Média"
          value={loading ? "—" : formatBitrate(average)}
          helper={`intervalo de ${selectedRange}`} />
        <SummaryCard
          label="Pico"
          value={loading ? "—" : formatBitrate(peak)}
          tone="warning" />
        <SummaryCard label="Amostras" value={loading ? "—" : data.length} />
      </SummaryCards>

      <section className="traffic-controls" aria-label="Controles do gráfico">
        <div className="traffic-control-group">
          <span className="traffic-control-label">Intervalo</span>
        {RANGE_OPTIONS.map((key) => (
          <button
            key={key}
            onClick={() => setSelectedRange(key)}
            className={`traffic-control-button ${selectedRange === key ? "active" : ""}`}
            aria-pressed={selectedRange === key}
          >
            {key}
          </button>
        ))}
        </div>

        <div className="traffic-control-group">
          <span className="traffic-control-label">Visualização</span>
        {[
          { id: "both", label: "Ambos" },
          { id: "rx", label: "Entrada" },
          { id: "tx", label: "Saída" },
        ].map((opt) => (
          <button
            key={opt.id}
            onClick={() => setViewMode(opt.id)}
            className={`traffic-control-button ${viewMode === opt.id ? "active" : ""}`}
            aria-pressed={viewMode === opt.id}
          >
            {opt.label}
          </button>
        ))}
        </div>
      </section>

      <div className="data-legend" aria-label="Legenda do gráfico">
        <span className="data-legend-title">Legenda:</span>
        <span><i className="traffic-legend-dot traffic-legend-rx" /> Entrada</span>
        <span><i className="traffic-legend-dot traffic-legend-tx" /> Saída</span>
      </div>

      {/* Gráfico */}
      <div className="traffic-chart-card">
        {loading ? (
          <p className="traffic-message">Carregando dados…</p>
        ) : error ? (
          <div className="traffic-message traffic-error" role="alert">
            <p>{error}</p>
            <button className="btn btn-primary" onClick={() => fetchData(selectedRange)}>
              Tentar novamente
            </button>
          </div>
        ) : data.length === 0 ? (
          <p className="traffic-message">Nenhuma amostra encontrada para o intervalo.</p>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data}>
              <XAxis
                dataKey="timestamp"
                tickFormatter={(value) => new Date(value * 1000).toLocaleTimeString(
                  "pt-BR", { hour: "2-digit", minute: "2-digit" }
                )}
              />
              <YAxis tickFormatter={formatBitrate} width={90} />
              <Tooltip
                formatter={(value) => formatBitrate(value)}
                labelFormatter={(value) => new Date(value * 1000).toLocaleString("pt-BR")}
              />
              <Legend />
              {(viewMode === "rx" || viewMode === "both") && (
                <Line
                  type="monotone"
                  dataKey="rx"
                  name="Entrada (Download)"
                  stroke="#16a34a"
                  strokeWidth={2}
                  dot={false}
                />
              )}
              {(viewMode === "tx" || viewMode === "both") && (
                <Line
                  type="monotone"
                  dataKey="tx"
                  name="Saída (Upload)"
                  stroke="#3187c9"
                  strokeWidth={2}
                  dot={false}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default TrafficAggregation;
